export type Language = 'en' | 'de';

export const messages: Record<Language, any> = {
  en: {
    organizationSettings: {
      title: 'Organisation settings',
      description: 'Manage your organisation’s details and reporting settings.',
      information: 'Organisation details',
      currencySection: 'Currency & reporting',
      link: 'Settings',
      back: 'Back to organisation',
      name: 'Organisation name',
      nameRequired: 'Enter an organisation name',
      city: 'City',
      optional: '(optional)',
      currency: 'Base currency',
      currencyNote: 'Changing the base currency affects historical transactions and reports. If a change is needed, please contact the software administrator.',
      loading: 'Loading organisation settings',
      loadError: 'Organisation settings could not be loaded.',
      retry: 'Try again',
      saveError: 'Organisation settings could not be saved.',
      saved: 'Organisation settings saved.',
      reloadHint: 'After saving, the application reloads to apply the settings everywhere.',
      reset: 'Discard changes',
    },
    workspaces: {
      timesheet: {
        title: 'Timesheet',
        description: 'Record working time and review monthly and annual totals.',
        monthly: 'Monthly',
        yearly: 'Yearly',
        onBehalf: 'Entries for {name}',
        onBehalfDescription: 'You are viewing another person’s timesheet.',
      },
      absences: {
        title: 'Absence calendar',
        description: 'Plan absences, review leave balances and see team availability.',
      },
    },
    common: {
      pagination: {
        label: 'Table pagination',
        previous: 'Previous',
        next: 'Next',
        page: 'Page {page} of {totalPages}',
        range: '{start}–{end} of {total} entries',
      },
      table: {
        filters: 'Table filters',
        clearFilters: 'Clear filters',
        results: '{count} results',
        sortAscending: 'Sort {column} ascending',
        sortDescending: 'Sort {column} descending',
        clearSort: 'Clear sorting for {column}',
        group: 'Group by {column}',
        ungroup: 'Stop grouping by {column}',
        expandGroup: 'Expand group',
        collapseGroup: 'Collapse group',
        scrollForMore: 'Scroll for more',
      },
      fileExchange: {
        title: 'Excel import / export',
        action: 'Import & Export',
        upload: 'Upload Excel file',
        drop: 'Drop a file',
        uploadHint: 'or click to upload',
        success: 'Uploaded successfully!',
      },
      export: {
        all: 'Export all entries',
        matching: 'Export all matching entries',
        page: 'Export current page',
        hint: 'Download Excel file',
      },
      featureLocked: {
        title: 'Feature locked',
        notReady: 'This feature is not available.',
        noAccess: 'You do not have access to this feature.',
      },
      cancel: 'Cancel',
      save: 'Save',
      submitting: 'Submitting...',
      language: {
        label: 'Language',
        english: 'English',
        german: 'German',
      },
      country: {
        label: 'Country',
        placeholder: 'Select country',
        required: 'Select a country',
      },
    },
    brand: {
      descriptor: 'Project & fund control for NGOs',
      home: 'Peregin home',
      organizationHome: '{organization} home',
    },
    header: {
      openMenu: 'Open menu',
      navigation: 'Navigation',
      login: 'Log in',
      profile: 'Profile',
      info: 'Info',
      logout: 'Log out',
      timer: {
        start: 'Start timer',
        stop: 'Stop timer',
      },
      open: {
        holidays: 'Public Holidays',
        worktime: 'Work Hours Calculator',
        rates: 'Exchange Rates',
      },
      public: {
        product: 'Product',
        howItWorks: 'How it works',
        forNgos: 'For NGOs',
        security: 'Security',
        resources: 'Resources',
      },
      nav: {
        home: 'Home',
        fundraising: 'Fundraising',
        fundraisingOverview: 'Fundraising Overview',
        fundraisingPerProject: 'Fundraising Per Project',
        commitments: 'Commitments',
        donors: 'Donors',
        partnerCarryover: 'Partner Equity Carryover',
        projectManagement: 'Projects',
        projectsOverview: 'Projects Overview',
        perProjectView: 'Per Project View',
        projects: 'Projects',
        partners: 'Partners',
        consultants: 'Consultants',
        expenses: 'Expenses',
        finance: 'Finances',
        projectFundsView: 'Project Funds View',
        fundAllocationView: 'Fund Allocation View',
        costCenters: 'Cost Centers',
        funds: 'Funds',
        fundAllocations: 'Fund Allocations',
        transfers: 'Transfers',
        invoiceExpenses: 'Expenses',
        administration: 'Administration',
        documentation: 'Documentation',
        users: 'Users Setup',
        allOrganizations: 'All organizations',
        approvals: 'Approvals',
        directReports: 'Direct Reports',
        timesheet: 'Timesheet',
        absences: 'Absence Calendar',
        organization: 'Organization',
        development: 'Development',
      },
    },
    expenseExtraction: {
      title: 'Expenses',
      experimental: 'Experimental',
      intro: 'Upload an invoice or receipt to preview the expense details detected by the invoice service.',
      privacy: 'Experimental preview: the file is sent to invoice.peregin.com for processing. The extracted values are not saved in Peregin.',
      upload: {
        title: 'Drop an invoice here',
        action: 'Select an invoice file',
        hint: 'or click to choose a file',
        formats: 'PDF, JPG, PNG, WebP or TIFF · maximum 15 MB',
      },
      actions: {
        process: 'Extract expense',
        processing: 'Extracting…',
        reset: 'Choose another file',
      },
      processingHint: 'Reading the document. Scanned files can take a little longer.',
      errors: {
        type: 'Choose a PDF or a supported image file.',
        size: 'The file is larger than 15 MB.',
        generic: 'The invoice could not be processed. Please try again.',
      },
      status: {
        parsed: 'Ready',
        needs_review: 'Review required',
        failed: 'Extraction failed',
      },
      result: {
        title: 'Detected expense',
        subtitle: 'Review the extracted values before using them.',
        confidence: '{confidence}% confidence',
        processedBy: '{provider} · {elapsed}',
        raw: 'Show raw service response',
      },
      fields: {
        supplier: 'Supplier',
        invoiceNumber: 'Invoice number',
        invoiceDate: 'Invoice date',
        dueDate: 'Due date',
        taxId: 'Supplier tax ID',
        purchaseOrder: 'Purchase order',
        subtotal: 'Subtotal',
        tax: 'Tax',
        total: 'Total',
      },
      validation: {
        title: 'Items to review',
      },
      lines: {
        title: 'Line items',
        description: 'Description',
        sku: 'SKU',
        quantity: 'Quantity',
        unitPrice: 'Unit price',
        amount: 'Amount',
      },
    },
    hero: {
      category: 'Project & fund control for NGOs',
      title: 'Know where every restricted fund goes.',
      subtitle: 'Connect commitments, project budgets, partner transfers, and expenses in one transparent view.',
      primaryCta: 'Book a demo',
      signupNote: 'Ready to get started? Create an account for your organization, or book a demo to explore Peregin together.',
      metadataTitle: 'Peregin | Project & fund control for NGOs',
      metadataDescription: 'Connect commitments, project budgets, restricted funds, partner transfers, expenses, and supporting documents in one transparent view.',
      visual: {
        alt: 'Project funding overview showing the budget, confirmed commitments, allocated funds, partner transfers, recorded expenses, and remaining financing gap.',
        demoData: 'Anonymized demo data',
        title: 'Project funding overview',
        projectName: 'Education access programme',
        period: '2026–2028',
        coverage: 'Funding coverage',
        budget: 'Project budget',
        commitments: 'Confirmed commitments',
        allocated: 'Allocated funds',
        gap: 'Financing gap',
        secured: 'Secured funding',
        transfers: 'Partner transfers',
        expenses: 'Recorded expenses',
        traceability: 'From secured funding to recorded spending',
      },
    },
    outcomes: {
      eyebrow: 'Project finance outcomes',
      title: 'Control project funding from commitment to report',
      intro: 'Peregin brings the financial steps around a project together, so finance and programme teams can work from the same traceable view.',
      items: {
        gaps: {
          title: 'See financing gaps early',
          text: 'Compare project budgets with confirmed commitments, allocated funds, and annual coverage before a shortfall becomes urgent.',
          link: 'View project coverage',
        },
        funds: {
          title: 'Keep restricted funds under control',
          text: 'Track fund balances, allocations, and movements by project, purpose, and budget year.',
          link: 'Follow fund allocations',
        },
        partners: {
          title: 'Make partner spending traceable',
          text: 'Track partner transfers and expenses by project, while supporting documents retain the same project context for reporting.',
          link: 'Follow partner spending',
        },
      },
      chain: {
        eyebrow: 'One funding chain',
        title: 'From funding promise to documented spending',
        intro: 'Each step keeps its project and purpose context, creating a clear path from the original funder to the final report.',
        nodes: {
          funder: { title: 'Funder', text: 'Funding source' },
          commitment: { title: 'Commitment', text: 'Confirmed promise' },
          projectFund: { title: 'Project / fund', text: 'Assigned purpose' },
          partnerTransfer: { title: 'Partner transfer', text: 'Money sent' },
          expenseDocument: { title: 'Expense / document', text: 'Spending evidence' },
          report: { title: 'Report', text: 'Traceable overview' },
        },
      },
    },
    customerFit: {
      eyebrow: 'Built for',
      title: 'For NGOs managing real project-funding complexity',
      intro: 'Peregin is designed for international NGOs and nonprofits with roughly 10–100 employees and more financial coordination than a set of disconnected spreadsheets can handle.',
      builtFor: {
        title: 'Organizations like yours',
        size: 'Around 10–100 employees',
        criteria: {
          projects: 'Multiple concurrent projects and funders',
          funds: 'Restricted or internally designated funding',
          partners: 'Local implementation partners',
          currencies: 'Multiple currencies and reporting periods',
          reconciliation: 'Spreadsheet reconciliation around an existing accounting system',
        },
        exchangeTitle: 'Supported file exchange',
        exchangeText: 'Selected finance workflows support Excel (.xlsx) upload and download. Partner documents support PDF or ZIP upload and download.',
      },
      operations: {
        eyebrow: 'Supporting operations',
        title: 'Useful administration without losing the finance focus',
        intro: 'These existing modules support day-to-day operations while remaining secondary to project and fund control.',
        items: {
          timesheets: {
            title: 'Timesheets',
            text: 'Record project time and review monthly and yearly summaries.',
          },
          absences: {
            title: 'Absences and approvals',
            text: 'Request absences and manage approval tasks through reporting lines.',
          },
          organization: {
            title: 'Organization and users',
            text: 'Manage users, roles, organization details, and reporting relationships.',
          },
          documents: {
            title: 'Partner document exchange',
            text: 'External partner accounts can exchange project-linked PDF or ZIP documents.',
          },
        },
      },
    },
    organization: {
      reportingLineDescription: 'The reporting line is used to approve absences and timesheet entries.',
    },
    overviews: {
      activeOnly: 'Only with active projects',
      filters: { year: 'Year', project: 'Project', partner: 'Partner' },
      partners: {
        title: 'Partners',
        description: 'Manage partner organisations and their contact details.',
        create: 'New partner',
        search: 'Search partners',
      },
      consultants: {
        title: 'Consultants',
        description: 'Manage consultants and their contact details.',
        create: 'New consultant',
        search: 'Search consultants',
      },
      expenses: {
        title: 'Expenses',
        description: 'Review partner expenses by project.',
        create: 'New expense',
      },
      funds: {
        title: 'Funds',
        description: 'Manage project funds and their funding sources.',
        create: 'New fund',
      },
      transfers: {
        title: 'Transfers',
        description: 'Review partner transfers by year and project.',
        create: 'New transfer',
      },
      commitments: {
        title: 'Commitments',
        description: 'Manage funding commitments by project and year.',
        create: 'New commitment',
      },
      fundAllocations: {
        title: 'Fund allocations',
        description: 'Allocate funds between projects and cost centres.',
        create: 'New allocation',
      },
      partnerCarryovers: {
        title: 'Partner carryovers',
        description: 'Track carryovers between a partner’s projects.',
        create: 'New carryover',
      },
    },
    forms: {
      sections: {
        assignment: 'Assignment',
        amountAndDate: 'Amount & date',
        amountsAndRate: 'Amounts & exchange rate',
        source: 'Source',
        target: 'Destination',
        address: 'Address',
        contact: 'Contact details',
      },
      partners: { description: 'Maintain organisation, contact and bank details.' },
      expenses: { description: 'Assign the expense to a partner and project, then enter its amount and date.', details: 'Expense details' },
      commitments: { description: 'Record the project, institution and amount of the commitment.', details: 'Commitment details' },
      funds: { description: 'Assign project funding and record the amount and funding year.' },
      fundAllocations: { description: 'Choose the source and destination for the fund allocation.', details: 'Allocation details' },
      partnerCarryovers: { description: 'Record a partner carryover between projects and review the converted total.' },
      donors: { description: 'Maintain donor information, address and contact details.', details: 'Donor details' },
      transfers: { description: 'Enter the transfer details and review the summary before saving.', summary: 'Live summary' },
    },
    partners: {
      details: {
        description: 'Organisation information, bank details, documents and partner logins.',
        edit: 'Edit partner',
        information: 'Organisation information',
        bankDetails: 'Bank details',
        tab: 'Partner',
        documents: 'Documents',
        users: 'Partner logins',
      },
    },
    reports: {
      projects: {
        title: 'Project overview',
        description: 'Compare project budgets, transfers and local expenses.',
      },
      fundraising: {
        title: 'Fundraising',
        description: 'Compare fundraising budgets, funding and coverage across projects.',
        coverageByProject: 'Coverage by project',
      },
      fundraisingProject: {
        title: 'Project fundraising',
        description: 'Review fundraising coverage and contributions across funding years.',
        coverageByYear: 'Coverage by funding year',
      },
      fundMoves: {
        title: 'Fund allocations',
        description: 'Review allocations between projects and balances with internal reserves.',
        reserves: 'Internal reserves',
        allocations: 'Allocations by funding year',
      },
      provisions: {
        title: 'Project funds',
        description: 'Review quarterly fund balances by project and cost centre.',
        budgetYear: 'Budget year',
        quarterlyBalances: 'Quarterly balances by project',
      },
    },
    projects: {
      details: {
        description: 'Project information, partners and annual budgets.',
        edit: 'Edit project',
        tab: 'Project',
        documents: 'Documents',
      },
      sections: {
        information: 'Project information',
        yearlyBudgets: 'Annual budgets',
        totalBudget: 'Total budget',
        projectBudget: 'Project budget',
        fundraisingBudget: 'Fundraising budget',
      },
      form: {
        create: 'New project',
        edit: 'Edit project',
        description: 'Set project details, partner organisations and annual budgets.',
        addBudget: 'Annual budget',
      },
      report: {
        title: 'Project report',
        description: 'Review project balances, budgets and transfers across funding years.',
        budgetAndTransfers: 'Budgets & transfers',
        exchangeDifferences: 'Transfers & exchange rate differences',
        selectProject: 'Select a project to view its report.',
      },
      overview: {
        title: 'Projects',
        description: 'Manage projects, partner organisations and budgets.',
        create: 'New project',
        search: 'Search projects',
        activeOnly: 'Active projects only',
        deleted: 'Project deleted',
        deleteHeading: 'Delete {name}',
        columns: {
          name: 'Name',
          partner: 'Partner',
          startDate: 'Start date',
          budget: 'Budget',
          projectBudget: 'Project {currency}',
          fundraisingBudget: 'Fundraising {currency}',
        },
      },
    },
    donors: {
      tabs: { overview: 'Donors', recurringPayments: 'Recurring payments' },
      overview: {
        create: 'New donor',
        description: 'Manage donor contacts, types and status.',
        createFirst: 'Create first donor',
        empty: 'No donors found.',
        loadError: 'Donors could not be loaded.',
        archived: 'Donor archived',
        archive: 'Archive',
        archiveHeading: 'Archive {name}',
        unnamed: 'Unnamed donor',
        manual: 'Manual',
        filters: {
          search: 'Search donors',
          kind: 'Filter by donor type',
          allKinds: 'All types',
          status: 'Filter by status',
          allStatuses: 'All statuses',
        },
        sort: {
          field: 'Sort field',
          direction: 'Sort direction',
          updatedAt: 'Last updated',
          createdAt: 'Created at',
          descending: 'Descending',
          ascending: 'Ascending',
        },
        status: { active: 'Active', archived: 'Archived' },
        columns: {
          name: 'Name',
          kind: 'Type',
          place: 'Place / country',
          contact: 'Primary contact',
          source: 'Source',
          status: 'Status',
        },
      },
      form: {
        title: {
          create: 'New donor',
          edit: 'Edit donor',
          view: 'View donor',
        },
        kind: {
          label: 'Type',
          placeholder: 'Select type',
          required: 'Select a type',
          options: {
            person: 'Person',
            household: 'Household',
            company: 'Company',
            foundation: 'Foundation',
            church: 'Church',
            municipality: 'Municipality',
            publicInstitution: 'Public institution',
            association: 'Association',
            other: 'Other',
          },
        },
        fields: {
          firstName: 'First name',
          lastName: 'Last name',
          organizationName: 'Organization',
          street: 'Street',
          streetAddition: 'Address line 2',
          postalCode: 'Postal code',
          city: 'City',
          email: 'Email',
          phone: 'Phone',
        },
        validation: {
          invalidEmail: 'Invalid email address',
          invalidPhone: 'Invalid phone number',
        },
        errors: {
          load: 'The donor could not be loaded.',
          save: 'The donor could not be saved.',
        },
      },
    },
      landing: {
        welcome: 'Great to see you, {name} 👋',
        workspace: {
          subtitle: 'Your tasks and shortcuts for the day.',
          quickActions: 'Quick actions',
          logTime: 'Log time',
          requestAbsence: 'Request absence',
          addExpense: 'Add expense',
          newProject: 'New project',
          organization: 'Manage organisation',
          attention: 'Needs your attention',
          loading: 'Loading your tasks',
          retry: 'Try again',
          returned: 'Review returned entries',
          returnedHint: 'Correct your rejected time and absence entries.',
          pendingTimesheets: 'Review pending timesheets',
          pendingTimesheetsHint: '{hours} h of timesheets',
          pendingAbsences: 'Review pending absences',
          pendingAbsencesHint: '{days} days of absences',
          allClear: 'You’re up to date',
          allClearHint: 'No pending approvals or returned entries.',
        },
        approvals: {
          text: '{pending} pending approvals: {hours} of timesheets and {days} of absences. Action required: {actionRequired} rejected entries.',
          open: 'Open approvals',
        },
        timeline: {
        title: 'Project timeline',
        loadError: 'The project timeline could not be loaded.',
        rangeLabel: 'Project timeframe',
        activeFuture: 'Active & Upcoming',
        activeOnly: 'Active only',
        all: 'All',
        emptyTitle: 'No projects available',
        emptyDescription: 'Project timelines and annual budgets will appear here when projects are available for this view.',
        budgetInfo: 'Each amount shows the total fundraising budget in {currency} for that year across all projects, regardless of the timeline filter.',
      },
      ratesTitle: 'Indicative exchange rates',
        publicHighlight: 'Calculate work hours, target hours, and vacation entitlement.',
        signupLink: 'Create an account',
        partner: {
          welcome: 'Welcome, {name}',
          title: 'Partner documents',
          documentsCount: 'Uploaded documents',
          lastUpdated: 'Last update',
          missingPartnerTitle: 'Partner missing',
          missingPartnerText: 'This account is not linked to a partner.',
          documentsTitle: 'Documents',
          upload: 'Upload document',
          file: 'File',
          selectFile: 'Select file',
          dropzoneTitle: 'Drag and drop a document here',
          dropzoneHelp: 'PDF or ZIP file',
          fileName: 'File',
          project: 'Project',
          projectPlaceholder: 'Select project',
          comment: 'Comment',
          updatedAt: 'Updated',
          fileSize: 'Size',
          actions: 'Actions',
          loading: 'Loading documents...',
          empty: 'No documents uploaded yet.',
          noProjects: 'No projects are available for this partner.',
          editDocument: 'Edit document',
          download: 'Download',
          edit: 'Edit',
          delete: 'Delete',
          deleteConfirm: 'Delete document',
          loadError: 'Documents could not be loaded.',
          uploadError: 'Document could not be uploaded.',
          saveError: 'Document could not be saved.',
          deleteError: 'Document could not be deleted.',
          downloadError: 'Document could not be downloaded.',
        },
      },
    approvals: {
      title: 'Approvals',
      subtitle: 'All pending approvals and rejected entries that need action.',
      loadError: 'Approval inbox could not be loaded.',
      summary: {
        pendingTimesheets: 'Pending timesheets',
        pendingAbsences: 'Pending absences',
        actionRequired: 'Action required',
      },
      tabs: {
        timesheets: 'Open timesheets',
        absences: 'Open absences',
        actionRequired: 'Action required',
      },
      sections: {
        rejectedTimesheets: 'Rejected timesheets',
        rejectedAbsences: 'Rejected absences',
      },
      empty: {
        pendingTimesheets: 'No open timesheets.',
        pendingAbsences: 'No open absences.',
        actionRequired: 'No rejected entries need action.',
      },
      actions: {
        approve: 'Approve',
        reject: 'Reject',
        edit: 'Edit / resubmit',
        viewContext: 'Overview',
        details: 'Details',
        reload: 'Refresh',
      },
      decisionReason: 'Rejection reason',
      decisionFailed: 'The decision could not be saved. Please try again.',
      decisionConflict: 'This entry has changed. Check its current status in the inbox before deciding again.',
      timesheetDetails: 'Timesheet details',
      absenceDetails: 'Absence details',
      balanceFailed: 'The employee’s leave balance could not be loaded.',
      absenceSaveFailed: 'The absence could not be saved. Please try again.',
      decidedAt: 'Decided on {date}',
      decidedBy: 'Decided by {name}',
    },
    footer: {
      country: 'Switzerland',
      contact: 'Contact',
      info: 'Info',
      development: 'Development',
    },
    contact: {
      imageAlt: 'Contact',
      heroTitle: 'Questions, ideas, or feedback?',
      heroText: 'Send us a message and we will get back to you as soon as possible.',
      title: 'Contact',
      subtitle: 'Tell us what you need help with.',
      name: 'Name',
      namePlaceholder: 'Your name',
      email: 'Email address',
      emailPlaceholder: 'you@example.org',
      message: 'Your message',
      messagePlaceholder: 'How can we help?',
      demoMessage: 'I would like to book a Peregin demo for my organization.',
      securityMessage: 'I have a question about Peregin security and data protection.',
      submit: 'Send message',
      sending: 'Sending...',
      errorTitle: 'Submission failed',
      serverError: 'We could not send your message. Please try again.',
      successTitle: 'Message sent',
      successStatus: 'Received',
      successText: 'We received your message and will get back to you as soon as possible.',
      newMessage: 'Write another message',
      nameRequired: 'Name is required',
      emailRequired: 'Email is required',
      messageRequired: 'Message is required',
      captchaRequired: 'Please solve the security check.',
      captchaInvalid: 'The math answer is incorrect. Please try again.',
    },
    workModelPreview: {
      locationTitle: 'Location',
      locationHint: 'The country determines the public holidays used to calculate working days and hours.',
    },
    publicHolidays: {
      title: 'Holidays',
      titleWithYear: 'Holidays {year}',
      total: 'Total',
      totalCount: '{count} total',
      totalHelp: 'Holidays in the list',
      balance: 'Balance',
      balanceHelp: 'Credited holiday days',
      workdays: 'Workdays',
      workdaysHelp: 'Credited on workdays',
      listTitle: 'Calendar',
      loading: 'Loading',
      empty: 'No holidays found.',
      errorTitle: 'Holidays could not be loaded',
      errorText: 'Check the year or country and try again.',
      zurich: 'Zurich',
      date: 'Date',
      weekday: 'Weekday',
      description: 'Description',
      notes: 'Notes',
      halfDay: 'Half day',
      weekend: 'Weekend',
      regional: 'Regional',
      public: 'Public holiday',
      custom: 'Custom holiday',
      override: 'Organization override',
      fullDay: 'Full day',
      once: 'One-off: {date}',
      annual: 'Every year on {date}',
      nthWeekday: 'Every year: {ordinal} {weekday} in {month}',
      ordinal1: '1st',
      ordinal2: '2nd',
      ordinal3: '3rd',
      ordinal4: '4th',
      ordinal5: '5th',
      validFrom: 'Valid from {from} (no end date)',
      validBetween: 'Valid {from} – {until} (inclusive)',
      rulesError: 'Holiday rule details could not be loaded. Calendar dates are still shown.',
      summaryError: 'The annual summary could not be loaded. Please try again.',
    },
    customHolidays: {
      title: 'Organization holidays · {organization}',
      add: 'Add holiday',
      edit: 'Edit',
      remove: 'Remove',
      end: 'End rule',
      editNamed: 'Edit {name}',
      removeNamed: 'Remove {name}',
      empty: 'No organization holiday rules for this year.',
      createTitle: 'Add organization holiday',
      editTitle: 'Edit organization holiday',
      removeTitle: 'Remove organization holiday',
      endTitle: 'End organization holiday',
      name: 'Name',
      ruleType: 'Rule type',
      once: 'One-off date',
      annual_date: 'Annual date',
      nth_weekday: 'Nth weekday of a month',
      month: 'Month',
      day: 'Day',
      ordinal: 'Occurrence',
      duration: 'Duration',
      validFrom: 'Valid from',
      validUntil: 'Valid until (inclusive, optional)',
      effectiveDate: 'Effective from',
      deleteConfirmation: 'Remove “{name}”? This future rule will be deleted.',
      endConfirmation: 'End “{name}” from the selected date?',
      editHistory: 'The earlier rule remains valid until the day before this effective date. A new version starts on that date.',
      endHistory: 'The rule stops applying on the effective date. Earlier dates are preserved.',
      readOnly: 'Historical one-off and expired rules are read-only.',
      futureDate: 'Choose a valid date after today (UTC), no later than 31 December 2500.',
      effectiveWithinValidity: 'The effective date must be within the existing validity interval.',
      invalidUntil: 'The end date must be valid and on or after the start date.',
      invalidName: 'Enter a name of 1–200 characters.',
      oneOffWithinValidity: 'The one-off date must be valid and within the validity interval.',
      invalidAnnualDate: 'Choose a valid month and day.',
      invalidRuleType: 'Choose an available rule type.',
      writeError: 'The holiday could not be saved. Please try again.',
    },
    login: {
      title: 'Log in',
      welcome: 'Welcome back 👋',
      email: 'Email',
      password: 'Password',
      rememberMe: 'Stay signed in',
      forgotPassword: 'Forgot password?',
      submit: 'Log in',
      loading: 'Logging in...',
      or: 'or',
      signInWithMicrosoft: 'Sign in with Microsoft',
      microsoftLoading: 'Opening Microsoft...',
      signInWithGoogle: 'Sign in with Google',
      googleLoading: 'Opening Google...',
      googleError: 'Google sign-in failed',
      oidcError: 'Sign-in failed',
      emailRequired: 'Email is required',
      passwordRequired: 'Password is required',
      invalidCredentials: 'Invalid credentials',
      connectionError: 'Connection problem',
    },
    logout: {
      imageAlt: 'Logout',
      heroTitle: 'See you next time',
      heroText: 'Your session has ended. Log back in when you want to continue working.',
      status: 'Session ended',
      title: 'Logged out',
      goodbyeNamed: 'Goodbye, {name}.',
      description: 'You have been signed out securely. Protected areas require a new login.',
      login: 'Log in',
      home: 'Home',
    },
    notFound: {
      eyebrow: 'Error 404',
      title: 'This page was not found.',
      description: 'The address you opened does not exist or has moved. Return to the home page or open the previous page.',
      home: 'Go home',
      back: 'Back',
      imageAlt: 'Desert landscape',
      imageCaption: 'Sometimes a path leads nowhere.',
    },
    signup: {
      headline: 'Unlock Efficiency.',
      headlineAccent: 'Free account.',
      intro: 'More time for your mission, less time on admin.',
      form: {
        title: 'Create Account',
        subtitle: 'Get started with your free workspace.',
        organizationSection: 'Your organisation',
        accountSection: 'Your administrator account',
        accountHint: 'You’ll use these details to sign in and manage your team.',
        country: 'Country',
        currency: 'Home currency',
        city: 'City',
        optional: '(optional)',
        cityPlaceholder: 'Organisation’s city',
        locationHint: 'Your organisation’s country and city are used to calculate working hours and public holidays.',
        countryRequired: 'Choose a country',
        currencyRequired: 'Choose a currency',
        company: 'Organisation name',
        email: 'Email',
        firstName: 'First Name',
        lastName: 'Last Name',
        newPassword: 'New Password',
        confirmPassword: 'Repeat Password',
        companyPlaceholder: 'Company',
        emailPlaceholder: 'email@company.com',
        emailPrivacyNotice: 'We’ll only use your email to set up your account. No spam.',
        firstNamePlaceholder: 'First name',
        lastNamePlaceholder: 'Last name',
        submit: 'Create Free Account',
        companyRequired: 'Company name is required',
        emailRequired: 'Email is required',
        firstNameRequired: 'First name is required',
        lastNameRequired: 'Last name is required',
        passwordRequired: 'Password is required',
        confirmRequired: 'Please repeat the password',
        passwordMismatch: 'Passwords do not match.',
        captchaRequired: 'Please solve the security check.',
        captchaInvalid: 'The math answer is incorrect. Please try again.',
        captchaTitle: 'Security Check',
        captchaRegenerate: 'New numbers',
        captchaHint: 'Prove you are human by solving the math problem.',
        short: 'too short',
        weak: 'weak',
        okay: 'okay',
        good: 'good',
        strong: 'strong',
      },
    },
  },
  de: {
    organizationSettings: {
      title: 'Organisationseinstellungen',
      description: 'Organisationsdaten und Einstellungen für die Berichte verwalten.',
      information: 'Organisationsdaten',
      currencySection: 'Währung & Berichte',
      link: 'Einstellungen',
      back: 'Zurück zur Organisation',
      name: 'Name der Organisation',
      nameRequired: 'Name der Organisation eingeben',
      city: 'Stadt',
      optional: '(optional)',
      currency: 'Basiswährung',
      currencyNote: 'Eine Änderung der Basiswährung wirkt sich auf historische Buchungen und Berichte aus. Falls eine Änderung nötig ist, wenden Sie sich bitte an den Softwareadministrator.',
      loading: 'Organisationseinstellungen werden geladen',
      loadError: 'Organisationseinstellungen konnten nicht geladen werden.',
      retry: 'Erneut versuchen',
      saveError: 'Organisationseinstellungen konnten nicht gespeichert werden.',
      saved: 'Organisationseinstellungen gespeichert.',
      reloadHint: 'Nach dem Speichern wird die Anwendung neu geladen, damit die Einstellungen überall übernommen werden.',
      reset: 'Änderungen verwerfen',
    },
    workspaces: {
      timesheet: {
        title: 'Zeiterfassung',
        description: 'Arbeitszeiten erfassen und Monats- sowie Jahressummen prüfen.',
        monthly: 'Monatlich',
        yearly: 'Jährlich',
        onBehalf: 'Einträge für {name}',
        onBehalfDescription: 'Sie sehen die Zeiterfassung einer anderen Person.',
      },
      absences: {
        title: 'Abwesenheitskalender',
        description: 'Abwesenheiten planen, Feriensaldi prüfen und die Verfügbarkeit im Team sehen.',
      },
    },
    common: {
      pagination: {
        label: 'Tabellennavigation',
        previous: 'Zurück',
        next: 'Weiter',
        page: 'Seite {page} von {totalPages}',
        range: '{start}–{end} von {total} Einträgen',
      },
      table: {
        filters: 'Tabellenfilter',
        clearFilters: 'Filter zurücksetzen',
        results: '{count} Ergebnisse',
        sortAscending: '{column} aufsteigend sortieren',
        sortDescending: '{column} absteigend sortieren',
        clearSort: 'Sortierung für {column} aufheben',
        group: 'Nach {column} gruppieren',
        ungroup: 'Gruppierung nach {column} aufheben',
        expandGroup: 'Gruppe aufklappen',
        collapseGroup: 'Gruppe zuklappen',
        scrollForMore: 'Scrollen für mehr',
      },
      fileExchange: {
        title: 'Excel-Import / Export',
        action: 'Import & Export',
        upload: 'Excel-Datei hochladen',
        drop: 'Datei ablegen',
        uploadHint: 'oder zum Hochladen klicken',
        success: 'Erfolgreich!',
      },
      export: {
        all: 'Alle Einträge exportieren',
        matching: 'Alle passenden Einträge exportieren',
        page: 'Aktuelle Seite exportieren',
        hint: 'Excel-Datei herunterladen',
      },
      featureLocked: {
        title: 'Funktion gesperrt',
        notReady: 'Diese Funktion ist nicht verfügbar.',
        noAccess: 'Du hast keinen Zugriff auf diese Funktion.',
      },
      cancel: 'Abbrechen',
      save: 'Speichern',
      submitting: 'Einreichung...',
      language: {
        label: 'Sprache',
        english: 'Englisch',
        german: 'Deutsch',
      },
      country: {
        label: 'Land',
        placeholder: 'Land auswählen',
        required: 'Land auswählen',
      },
    },
    brand: {
      descriptor: 'Projekt- und Fondscontrolling für NGOs',
      home: 'Peregin Startseite',
      organizationHome: '{organization} Startseite',
    },
    header: {
      openMenu: 'Menü öffnen',
      navigation: 'Navigation',
      login: 'Anmelden',
      profile: 'Profil',
      info: 'Info',
      logout: 'Abmelden',
      timer: {
        start: 'Timer starten',
        stop: 'Timer stoppen',
      },
      open: {
        holidays: 'Feiertage',
        worktime: 'Arbeitszeit-Rechner',
        rates: 'Währungskurs',
      },
      public: {
        product: 'Produkt',
        howItWorks: 'So funktioniert es',
        forNgos: 'Für NGOs',
        security: 'Sicherheit',
        resources: 'Ressourcen',
      },
      nav: {
        home: 'Startseite',
        fundraising: 'Fundraising',
        fundraisingOverview: 'Fundraising Übersicht',
        fundraisingPerProject: 'Fundraising Pro Projekt',
        commitments: 'Zusagen',
        donors: 'Spender',
        partnerCarryover: 'Eigenmittel Übertrag Partner',
        projectManagement: 'Projektverwaltung',
        projectsOverview: 'Projekte Übersicht',
        perProjectView: 'Ansicht Pro Projekt',
        projects: 'Projekte',
        partners: 'Partner',
        consultants: 'Konsulenten',
        expenses: 'Ausgaben',
        finance: 'Finanzverwaltung',
        projectFundsView: 'Ansicht Projektfonds',
        fundAllocationView: 'Ansicht Zuweisung Fonds',
        costCenters: 'Kostenstellen',
        funds: 'Fonds',
        fundAllocations: 'Zuweisung Fonds',
        transfers: 'Transfers',
        invoiceExpenses: 'Spesen',
        administration: 'Administration',
        documentation: 'Dokumentation',
        users: 'Benutzereinrichtung',
        allOrganizations: 'Alle Organisationen',
        approvals: 'Genehmigungen',
        directReports: 'Direkte Berichte',
        timesheet: 'Zeiterfassung',
        absences: 'Abwesenheitskalender',
        organization: 'Organisation',
        development: 'Entwicklung',
      },
    },
    expenseExtraction: {
      title: 'Spesen',
      experimental: 'Experimentell',
      intro: 'Rechnung oder Beleg hochladen und erkannte Spesendaten direkt prüfen.',
      privacy: 'Experimentelle Vorschau: Die Datei wird zur Verarbeitung an invoice.peregin.com gesendet. Die erkannten Werte werden nicht in Peregin gespeichert.',
      upload: {
        title: 'Rechnung hier ablegen',
        action: 'Rechnungsdatei auswählen',
        hint: 'oder klicken, um eine Datei auszuwählen',
        formats: 'PDF, JPG, PNG, WebP oder TIFF · maximal 15 MB',
      },
      actions: {
        process: 'Spesen erkennen',
        processing: 'Wird erkannt…',
        reset: 'Andere Datei wählen',
      },
      processingHint: 'Das Dokument wird gelesen. Bei gescannten Dateien kann dies etwas länger dauern.',
      errors: {
        type: 'Wählen Sie eine PDF-Datei oder ein unterstütztes Bildformat.',
        size: 'Die Datei ist grösser als 15 MB.',
        generic: 'Die Rechnung konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut.',
      },
      status: {
        parsed: 'Bereit',
        needs_review: 'Prüfung erforderlich',
        failed: 'Erkennung fehlgeschlagen',
      },
      result: {
        title: 'Erkannte Spesen',
        subtitle: 'Prüfen Sie die erkannten Werte vor der weiteren Verwendung.',
        confidence: '{confidence} % Konfidenz',
        processedBy: '{provider} · {elapsed}',
        raw: 'Unverarbeitete Antwort des Dienstes anzeigen',
      },
      fields: {
        supplier: 'Lieferant',
        invoiceNumber: 'Rechnungsnummer',
        invoiceDate: 'Rechnungsdatum',
        dueDate: 'Fälligkeitsdatum',
        taxId: 'Steuernummer Lieferant',
        purchaseOrder: 'Bestellnummer',
        subtotal: 'Zwischensumme',
        tax: 'Steuer',
        total: 'Gesamtbetrag',
      },
      validation: {
        title: 'Zu prüfende Angaben',
      },
      lines: {
        title: 'Positionen',
        description: 'Beschreibung',
        sku: 'Artikelnummer',
        quantity: 'Menge',
        unitPrice: 'Einzelpreis',
        amount: 'Betrag',
      },
    },
    hero: {
      category: 'Projekt- und Fondscontrolling für NGOs',
      title: 'Von der Förderzusage bis zum Partnerbeleg: jede Projektmittelbewegung im Blick.',
      subtitle: 'Peregin verbindet Budgets, Zusagen, Fonds, Partnertransfers und Ausgaben in einer nachvollziehbaren Übersicht - ohne Excel-Abgleich.',
      primaryCta: 'Demo vereinbaren',
      signupNote: 'Bereit loszulegen? Erstellen Sie ein Konto für Ihre Organisation oder vereinbaren Sie eine Demo, um Peregin gemeinsam kennenzulernen.',
      metadataTitle: 'Peregin | Projekt- und Fondscontrolling für NGOs',
      metadataDescription: 'Peregin verbindet Budgets, Zusagen, zweckgebundene Fonds, Partnertransfers, Ausgaben und Belege in einer nachvollziehbaren Übersicht.',
      visual: {
        alt: 'Projektfinanzierungsübersicht mit Budget, bestätigten Zusagen, zugewiesenen Fonds, Partnertransfers, erfassten Ausgaben und verbleibender Finanzierungslücke.',
        demoData: 'Anonymisierte Demodaten',
        title: 'Projektfinanzierungsübersicht',
        projectName: 'Programm für Bildungszugang',
        period: '2026–2028',
        coverage: 'Finanzierungsdeckung',
        budget: 'Projektbudget',
        commitments: 'Bestätigte Zusagen',
        allocated: 'Zugewiesene Fonds',
        gap: 'Finanzierungslücke',
        secured: 'Gesicherte Finanzierung',
        transfers: 'Partnertransfers',
        expenses: 'Erfasste Ausgaben',
        traceability: 'Von der gesicherten Finanzierung zur erfassten Ausgabe',
      },
    },
    outcomes: {
      eyebrow: 'Ergebnisse im Projektcontrolling',
      title: 'Projektmittel von der Zusage bis zum Bericht steuern',
      intro: 'Peregin führt die finanziellen Schritte rund um ein Projekt zusammen, damit Finanz- und Programmteams mit derselben nachvollziehbaren Übersicht arbeiten.',
      items: {
        gaps: {
          title: 'Finanzierungslücken früh erkennen',
          text: 'Projektbudgets, bestätigte Zusagen, zugewiesene Fonds und die Jahresdeckung vergleichen, bevor eine Lücke dringend wird.',
          link: 'Projektdeckung ansehen',
        },
        funds: {
          title: 'Zweckgebundene Fonds unter Kontrolle halten',
          text: 'Fondssalden, Zuweisungen und Bewegungen nach Projekt, Zweck und Budgetjahr verfolgen.',
          link: 'Fondszuweisungen verfolgen',
        },
        partners: {
          title: 'Partnerausgaben nachvollziehbar machen',
          text: 'Partnertransfers und Ausgaben nach Projekt verfolgen; Belege behalten denselben Projektbezug für die Berichterstattung.',
          link: 'Partnerausgaben verfolgen',
        },
      },
      chain: {
        eyebrow: 'Eine Finanzierungskette',
        title: 'Von der Förderzusage bis zur dokumentierten Ausgabe',
        intro: 'Jeder Schritt behält seinen Projekt- und Zweckbezug. So entsteht ein klarer Weg vom ursprünglichen Förderer bis zum Bericht.',
        nodes: {
          funder: { title: 'Förderer', text: 'Finanzierungsquelle' },
          commitment: { title: 'Förderzusage', text: 'Bestätigte Zusage' },
          projectFund: { title: 'Projekt / Fonds', text: 'Zugewiesener Zweck' },
          partnerTransfer: { title: 'Partnertransfer', text: 'Überwiesene Mittel' },
          expenseDocument: { title: 'Ausgabe / Beleg', text: 'Ausgabennachweis' },
          report: { title: 'Bericht', text: 'Nachvollziehbare Übersicht' },
        },
      },
    },
    customerFit: {
      eyebrow: 'Ausgelegt für',
      title: 'Für NGOs mit anspruchsvoller Projektfinanzierung',
      intro: 'Peregin ist für international tätige NGOs und Nonprofits mit rund 10–100 Mitarbeitenden konzipiert, deren Finanzkoordination nicht mehr in voneinander getrennte Tabellen passt.',
      builtFor: {
        title: 'Organisationen wie Ihre',
        size: 'Rund 10–100 Mitarbeitende',
        criteria: {
          projects: 'Mehrere gleichzeitig laufende Projekte und Förderer',
          funds: 'Zweckgebundene oder intern zugewiesene Mittel',
          partners: 'Lokale Umsetzungspartner',
          currencies: 'Mehrere Währungen und Berichtszeiträume',
          reconciliation: 'Abstimmung von Tabellen mit einem bestehenden Buchhaltungssystem',
        },
        exchangeTitle: 'Unterstützter Dateiaustausch',
        exchangeText: 'Ausgewählte Finanzabläufe unterstützen Excel-Dateien (.xlsx) für Upload und Download. Partnerdokumente können als PDF oder ZIP hoch- und heruntergeladen werden.',
      },
      operations: {
        eyebrow: 'Unterstützende Administration',
        title: 'Nützliche Administration, ohne den Finanzfokus zu verlieren',
        intro: 'Diese bestehenden Module unterstützen den Arbeitsalltag und bleiben dem Projekt- und Fondscontrolling visuell untergeordnet.',
        items: {
          timesheets: {
            title: 'Zeiterfassung',
            text: 'Projektzeiten erfassen und Monats- sowie Jahresübersichten prüfen.',
          },
          absences: {
            title: 'Abwesenheiten und Genehmigungen',
            text: 'Abwesenheiten beantragen und Genehmigungen über Berichtslinien bearbeiten.',
          },
          organization: {
            title: 'Organisation und Benutzer',
            text: 'Benutzer, Rollen, Organisationsdetails und Berichtsbeziehungen verwalten.',
          },
          documents: {
            title: 'Dokumentenaustausch mit Partnern',
            text: 'Externe Partnerkonten können projektbezogene PDF- oder ZIP-Dokumente austauschen.',
          },
        },
      },
    },
    organization: {
      reportingLineDescription: 'Die Berichtslinie wird verwendet, um Abwesenheiten und Zeiterfassungseinträge zu genehmigen.',
    },
    overviews: {
      activeOnly: 'Nur mit aktiven Projekten',
      filters: { year: 'Jahr', project: 'Projekt', partner: 'Partner' },
      partners: {
        title: 'Partner',
        description: 'Partnerorganisationen und ihre Kontaktdaten verwalten.',
        create: 'Neuer Partner',
        search: 'Partner suchen',
      },
      consultants: {
        title: 'Konsulenten',
        description: 'Konsulenten und ihre Kontaktdaten verwalten.',
        create: 'Neuer Konsulent',
        search: 'Konsulenten suchen',
      },
      expenses: {
        title: 'Ausgaben',
        description: 'Ausgaben der Partner nach Projekt prüfen.',
        create: 'Neue Ausgabe',
      },
      funds: {
        title: 'Fonds',
        description: 'Projektfonds und ihre Mittelherkunft verwalten.',
        create: 'Neuer Fonds',
      },
      transfers: {
        title: 'Transfers',
        description: 'Überweisungen an Partner nach Jahr und Projekt prüfen.',
        create: 'Neuer Transfer',
      },
      commitments: {
        title: 'Zusagen',
        description: 'Förderzusagen nach Projekt und Jahr verwalten.',
        create: 'Neue Zusage',
      },
      fundAllocations: {
        title: 'Zuweisung Fonds',
        description: 'Mittel zwischen Projekten und Kostenstellen zuweisen.',
        create: 'Neue Fondsänderung',
      },
      partnerCarryovers: {
        title: 'Eigenmittel Übertrag Partner',
        description: 'Eigenmittelüberträge zwischen Projekten eines Partners verfolgen.',
        create: 'Neuer Übertrag',
      },
    },
    forms: {
      sections: {
        assignment: 'Zuordnung',
        amountAndDate: 'Betrag & Datum',
        amountsAndRate: 'Beträge & Kurs',
        source: 'Herkunft',
        target: 'Ziel',
        address: 'Adresse',
        contact: 'Kontaktdaten',
      },
      partners: { description: 'Organisationsdaten, Kontakt und Bankverbindung pflegen.' },
      expenses: { description: 'Ausgabe einem Partner und Projekt zuordnen und Betrag sowie Datum erfassen.', details: 'Ausgabendetails' },
      commitments: { description: 'Projekt, Institution und Betrag der Zusage erfassen.', details: 'Zusagedetails' },
      funds: { description: 'Projektmittel zuordnen und Betrag sowie Fondsjahr erfassen.' },
      fundAllocations: { description: 'Herkunft und Ziel der Fondszuweisung festlegen.', details: 'Zuweisungsdetails' },
      partnerCarryovers: { description: 'Eigenmittelübertrag eines Partners zwischen Projekten erfassen und die umgerechnete Summe prüfen.' },
      donors: { description: 'Spenderdaten, Adresse und Kontaktinformationen pflegen.', details: 'Spenderdaten' },
      transfers: { description: 'Transferdaten erfassen und die Zusammenfassung vor dem Speichern prüfen.', summary: 'Live-Übersicht' },
    },
    partners: {
      details: {
        description: 'Organisationsdaten, Bankverbindung, Dokumente und Partneranmeldungen.',
        edit: 'Bearbeiten',
        information: 'Organisationsdaten',
        bankDetails: 'Bankverbindung',
        tab: 'Partner',
        documents: 'Dokumente',
        users: 'Partner Anmeldung',
      },
    },
    reports: {
      projects: {
        title: 'Projekte Übersicht',
        description: 'Projektbudgets, Transfers und lokale Ausgaben vergleichen.',
      },
      fundraising: {
        title: 'Fundraising',
        description: 'Fundraisingbudgets, Mittel und Deckung der Projekte vergleichen.',
        coverageByProject: 'Deckung nach Projekt',
      },
      fundraisingProject: {
        title: 'Fundraising Projekt',
        description: 'Fundraisingdeckung und Beiträge über die Mitteljahre hinweg prüfen.',
        coverageByYear: 'Deckung nach Mitteljahr',
      },
      fundMoves: {
        title: 'Zuweisung Fonds',
        description: 'Zuweisungen zwischen Projekten und Bestände bei internen Reserven prüfen.',
        reserves: 'Interne Reserven',
        allocations: 'Zuweisungen nach Mitteljahr',
      },
      provisions: {
        title: 'Projektfonds',
        description: 'Quartalsbestände der Fonds nach Projekt und Kostenstelle prüfen.',
        budgetYear: 'Budgetjahr',
        quarterlyBalances: 'Quartalsbestände nach Projekt',
      },
    },
    projects: {
      details: {
        description: 'Projektinformationen, Partner und Jahresbudgets.',
        edit: 'Bearbeiten',
        tab: 'Projekt',
        documents: 'Dokumente',
      },
      sections: {
        information: 'Projektinformationen',
        yearlyBudgets: 'Jahresbudgets',
        totalBudget: 'Gesamtbudget',
        projectBudget: 'Projektbudget',
        fundraisingBudget: 'Fundraisingbudget',
      },
      form: {
        create: 'Neues Projekt',
        edit: 'Projekt Bearbeiten',
        description: 'Projektdaten, Partnerorganisationen und Jahresbudgets festlegen.',
        addBudget: 'Jahresbudget',
      },
      report: {
        title: 'Projektbericht',
        description: 'Projektbestände, Budgets und Transfers über die Mitteljahre hinweg prüfen.',
        budgetAndTransfers: 'Budgets & Transfers',
        exchangeDifferences: 'Transfers & Kursabweichung',
        selectProject: 'Wählen Sie ein Projekt, um den Bericht anzuzeigen.',
      },
      overview: {
        title: 'Projekte',
        description: 'Projekte, Partnerorganisationen und Budgets verwalten.',
        create: 'Neues Projekt',
        search: 'Projekte suchen',
        activeOnly: 'Nur aktive Projekte',
        deleted: 'Projekt gelöscht',
        deleteHeading: '{name} löschen',
        columns: {
          name: 'Name',
          partner: 'Partner',
          startDate: 'Anfang',
          budget: 'Budget',
          projectBudget: 'Projekt {currency}',
          fundraisingBudget: 'Fundraising {currency}',
        },
      },
    },
    donors: {
      tabs: { overview: 'Spender', recurringPayments: 'Wiederkehrende Zahlungen' },
      overview: {
        create: 'Neuer Spender',
        description: 'Spenderkontakte, Spenderarten und Status verwalten.',
        createFirst: 'Ersten Spender erfassen',
        empty: 'Keine Spender gefunden.',
        loadError: 'Spender konnten nicht geladen werden.',
        archived: 'Spender archiviert',
        archive: 'Archivieren',
        archiveHeading: '{name} archivieren',
        unnamed: 'Ohne Namen',
        manual: 'Manuell',
        filters: {
          search: 'Spender suchen',
          kind: 'Nach Spenderart filtern',
          allKinds: 'Alle Typen',
          status: 'Nach Status filtern',
          allStatuses: 'Alle Status',
        },
        sort: {
          field: 'Sortierung',
          direction: 'Sortierreihenfolge',
          updatedAt: 'Zuletzt geändert',
          createdAt: 'Erstellt am',
          descending: 'Absteigend',
          ascending: 'Aufsteigend',
        },
        status: { active: 'Aktiv', archived: 'Archiviert' },
        columns: {
          name: 'Name',
          kind: 'Typ',
          place: 'Ort / Land',
          contact: 'Primärer Kontakt',
          source: 'Quelle',
          status: 'Status',
        },
      },
      form: {
        title: {
          create: 'Neuer Spender',
          edit: 'Spender bearbeiten',
          view: 'Spender ansehen',
        },
        kind: {
          label: 'Typ',
          placeholder: 'Typ auswählen',
          required: 'Typ auswählen',
          options: {
            person: 'Person',
            household: 'Haushalt',
            company: 'Unternehmen',
            foundation: 'Stiftung',
            church: 'Kirche',
            municipality: 'Gemeinde',
            publicInstitution: 'Öffentliche Institution',
            association: 'Verein',
            other: 'Andere',
          },
        },
        fields: {
          firstName: 'Vorname',
          lastName: 'Nachname',
          organizationName: 'Organisation',
          street: 'Straße',
          streetAddition: 'Adresszusatz',
          postalCode: 'Postleitzahl',
          city: 'Ort',
          email: 'E-Mail',
          phone: 'Telefon',
        },
        validation: {
          invalidEmail: 'Ungültige E-Mail-Adresse',
          invalidPhone: 'Ungültige Telefonnummer',
        },
        errors: {
          load: 'Spender konnte nicht geladen werden.',
          save: 'Spender konnte nicht gespeichert werden.',
        },
      },
    },
    landing: {
      welcome: 'Schön dich zu sehen, {name} 👋',
      workspace: {
        subtitle: 'Deine Aufgaben und Schnellzugriffe für den Tag.',
        quickActions: 'Schnellzugriffe',
        logTime: 'Zeit erfassen',
        requestAbsence: 'Abwesenheit beantragen',
        addExpense: 'Ausgabe erfassen',
        newProject: 'Neues Projekt',
        organization: 'Organisation verwalten',
        attention: 'Das steht an',
        loading: 'Deine Aufgaben werden geladen',
        retry: 'Erneut versuchen',
        returned: 'Zurückgewiesene Einträge prüfen',
        returnedHint: 'Korrigiere deine abgelehnten Zeit- und Abwesenheitseinträge.',
        pendingTimesheets: 'Offene Zeiterfassung prüfen',
        pendingTimesheetsHint: '{hours} h Zeiterfassung',
        pendingAbsences: 'Offene Abwesenheiten prüfen',
        pendingAbsencesHint: '{days} Tage Abwesenheit',
        allClear: 'Du bist auf dem Laufenden',
        allClearHint: 'Keine offenen Genehmigungen oder zurückgewiesenen Einträge.',
      },
      approvals: {
        text: '{pending} offene Genehmigungen: {hours} Zeiterfassung und {days} Abwesenheiten. Aktion erforderlich: {actionRequired} abgelehnte Einträge.',
        open: 'Genehmigungen öffnen',
      },
      timeline: {
        title: 'Projektzeitplan',
        loadError: 'Der Projektzeitplan konnte nicht geladen werden.',
        rangeLabel: 'Projektzeitraum',
        activeFuture: 'Aktive & Zukunft',
        activeOnly: 'Nur die Aktiven',
        all: 'Alle',
        emptyTitle: 'Keine Projekte verfügbar',
        emptyDescription: 'Projektzeitpläne und Jahresbudgets erscheinen hier, sobald Projekte für diese Ansicht verfügbar sind.',
        budgetInfo: 'Jeder Betrag zeigt das gesamte Fundraising-Budget in {currency} für das jeweilige Jahr über alle Projekte, unabhängig vom gewählten Zeitstrahlfilter.',
      },
      ratesTitle: 'Indikative Wechselkurse',
      publicHighlight: 'Arbeitszeit, Sollstunden und Anspruch von Ferientagen berechnen.',
      signupLink: 'Konto erstellen',
      partner: {
        welcome: 'Willkommen, {name}',
        title: 'Partner Dokumente',
        documentsCount: 'Hochgeladene Dokumente',
        lastUpdated: 'Letzte Aktualisierung',
        missingPartnerTitle: 'Partner fehlt',
        missingPartnerText: 'Dieses Konto ist keinem Partner zugeordnet.',
        documentsTitle: 'Dokumente',
        upload: 'Dokument hochladen',
        file: 'Datei',
        selectFile: 'Datei auswählen',
        dropzoneTitle: 'Dokument hier ablegen',
        dropzoneHelp: 'PDF- oder ZIP-Datei',
        fileName: 'Datei',
        project: 'Projekt',
        projectPlaceholder: 'Projekt auswählen',
        comment: 'Kommentar',
        updatedAt: 'Aktualisiert',
        fileSize: 'Grösse',
        actions: 'Aktionen',
        loading: 'Dokumente werden geladen...',
        empty: 'Noch keine Dokumente hochgeladen.',
        noProjects: 'Für diesen Partner sind keine Projekte verfügbar.',
        editDocument: 'Dokument bearbeiten',
        download: 'Herunterladen',
        edit: 'Bearbeiten',
        delete: 'Löschen',
        deleteConfirm: 'Dokument löschen',
        loadError: 'Dokumente konnten nicht geladen werden.',
        uploadError: 'Dokument konnte nicht hochgeladen werden.',
        saveError: 'Dokument konnte nicht gespeichert werden.',
        deleteError: 'Dokument konnte nicht gelöscht werden.',
        downloadError: 'Dokument konnte nicht heruntergeladen werden.',
      },
    },
    approvals: {
      title: 'Genehmigungen',
      subtitle: 'Alle offenen Genehmigungen und abgelehnten Einträge mit Handlungsbedarf.',
      loadError: 'Genehmigungsübersicht konnte nicht geladen werden.',
      summary: {
        pendingTimesheets: 'Offene Zeiterfassung',
        pendingAbsences: 'Offene Abwesenheiten',
        actionRequired: 'Aktion erforderlich',
      },
      tabs: {
        timesheets: 'Offene Zeiterfassung',
        absences: 'Offene Abwesenheiten',
        actionRequired: 'Aktion erforderlich',
      },
      sections: {
        rejectedTimesheets: 'Abgelehnte Zeiterfassung',
        rejectedAbsences: 'Abgelehnte Abwesenheiten',
      },
      empty: {
        pendingTimesheets: 'Keine offene Zeiterfassung.',
        pendingAbsences: 'Keine offenen Abwesenheiten.',
        actionRequired: 'Keine abgelehnten Einträge mit Handlungsbedarf.',
      },
      actions: {
        approve: 'Genehmigen',
        reject: 'Ablehnen',
        edit: 'Bearbeiten / erneut einreichen',
        viewContext: 'Übersicht',
        details: 'Details',
        reload: 'Aktualisieren',
      },
      decisionReason: 'Ablehnungsgrund',
      decisionFailed: 'Die Entscheidung konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.',
      decisionConflict: 'Dieser Eintrag wurde geändert. Prüfen Sie den aktuellen Status im Posteingang, bevor Sie erneut entscheiden.',
      timesheetDetails: 'Details zur Zeiterfassung',
      absenceDetails: 'Details zur Abwesenheit',
      balanceFailed: 'Der Feriensaldo der ausgewählten Person konnte nicht geladen werden.',
      absenceSaveFailed: 'Die Abwesenheit konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.',
      decidedAt: 'Entschieden am {date}',
      decidedBy: 'Entschieden von {name}',
    },
    footer: {
      country: 'Schweiz',
      contact: 'Kontakt',
      info: 'Info',
      development: 'Entwicklung',
    },
    contact: {
      imageAlt: 'Kontakt',
      heroTitle: 'Fragen, Ideen oder Feedback?',
      heroText: 'Schreib uns eine Nachricht. Wir melden uns so schnell wie möglich.',
      title: 'Kontakt',
      subtitle: 'Sag uns, wobei wir helfen können.',
      name: 'Name',
      namePlaceholder: 'Dein Name',
      email: 'E-Mail-Adresse',
      emailPlaceholder: 'du@beispiel.org',
      message: 'Deine Nachricht',
      messagePlaceholder: 'Wie können wir helfen?',
      demoMessage: 'Ich möchte eine Peregin-Demo für meine Organisation vereinbaren.',
      securityMessage: 'Ich habe eine Frage zu Sicherheit und Datenschutz bei Peregin.',
      submit: 'Nachricht senden',
      sending: 'Wird gesendet...',
      errorTitle: 'Senden fehlgeschlagen',
      serverError: 'Deine Nachricht konnte nicht gesendet werden. Bitte versuche es erneut.',
      successTitle: 'Nachricht gesendet',
      successStatus: 'Erhalten',
      successText: 'Wir haben deine Nachricht erhalten und melden uns schnellstmöglich bei dir.',
      newMessage: 'Weitere Nachricht schreiben',
      nameRequired: 'Name ist erforderlich',
      emailRequired: 'E-Mail ist erforderlich',
      messageRequired: 'Nachricht ist erforderlich',
      captchaRequired: 'Bitte löse die Sicherheitsprüfung.',
      captchaInvalid: 'Die Rechenaufgabe ist falsch. Bitte erneut versuchen.',
    },
    workModelPreview: {
      locationTitle: 'Standort',
      locationHint: 'Das Land bestimmt die Feiertage für die Berechnung der Arbeitstage und Arbeitsstunden.',
    },
    publicHolidays: {
      title: 'Feiertage',
      titleWithYear: 'Feiertage {year}',
      total: 'Gesamt',
      totalCount: '{count} gesamt',
      totalHelp: 'Feiertage in der Liste',
      balance: 'Saldo',
      balanceHelp: 'Anrechenbare Feiertage',
      workdays: 'Werktage',
      workdaysHelp: 'Anrechenbar an Werktagen',
      listTitle: 'Kalender',
      loading: 'Lädt',
      empty: 'Keine Feiertage gefunden.',
      errorTitle: 'Feiertage konnten nicht geladen werden',
      errorText: 'Bitte Jahr oder Land prüfen und erneut versuchen.',
      zurich: 'Zürich',
      date: 'Datum',
      weekday: 'Wochentag',
      description: 'Beschreibung',
      notes: 'Anmerkungen',
      halfDay: 'Halbtag',
      weekend: 'Wochenende',
      regional: 'Regional',
      public: 'Öffentlicher Feiertag',
      custom: 'Eigener Feiertag',
      override: 'Organisationsanpassung',
      fullDay: 'Ganztag',
      once: 'Einmalig: {date}',
      annual: 'Jährlich am {date}',
      nthWeekday: 'Jährlich: {ordinal} {weekday} im {month}',
      ordinal1: '1.',
      ordinal2: '2.',
      ordinal3: '3.',
      ordinal4: '4.',
      ordinal5: '5.',
      validFrom: 'Gültig ab {from} (ohne Enddatum)',
      validBetween: 'Gültig {from} – {until} (einschliesslich)',
      rulesError: 'Feiertagsregeln konnten nicht geladen werden. Die Kalenderdaten werden weiterhin angezeigt.',
      summaryError: 'Die Jahresübersicht konnte nicht geladen werden. Bitte erneut versuchen.',
    },
    customHolidays: {
      title: 'Organisationsfeiertage · {organization}',
      add: 'Feiertag hinzufügen',
      edit: 'Bearbeiten',
      remove: 'Entfernen',
      end: 'Regel beenden',
      editNamed: '{name} bearbeiten',
      removeNamed: '{name} entfernen',
      empty: 'Keine Organisationsregeln für dieses Jahr.',
      createTitle: 'Organisationsfeiertag hinzufügen',
      editTitle: 'Organisationsfeiertag bearbeiten',
      removeTitle: 'Organisationsfeiertag entfernen',
      endTitle: 'Organisationsfeiertag beenden',
      name: 'Name',
      ruleType: 'Regeltyp',
      once: 'Einmaliges Datum',
      annual_date: 'Jährliches Datum',
      nth_weekday: 'N-ter Wochentag eines Monats',
      month: 'Monat',
      day: 'Tag',
      ordinal: 'Vorkommen',
      duration: 'Dauer',
      validFrom: 'Gültig ab',
      validUntil: 'Gültig bis (einschliesslich, optional)',
      effectiveDate: 'Wirksam ab',
      deleteConfirmation: '“{name}” entfernen? Diese zukünftige Regel wird gelöscht.',
      endConfirmation: '“{name}” ab dem gewählten Datum beenden?',
      editHistory: 'Die bisherige Regel gilt bis zum Vortag des Wirksamkeitsdatums. An diesem Datum beginnt eine neue Version.',
      endHistory: 'Ab dem Wirksamkeitsdatum gilt die Regel nicht mehr. Frühere Daten bleiben erhalten.',
      readOnly: 'Historische einmalige und abgelaufene Regeln sind schreibgeschützt.',
      futureDate: 'Wähle ein gültiges Datum nach heute (UTC), spätestens am 31. Dezember 2500.',
      effectiveWithinValidity: 'Das Wirksamkeitsdatum muss im bisherigen Gültigkeitszeitraum liegen.',
      invalidUntil: 'Das Enddatum muss gültig sein und am oder nach dem Startdatum liegen.',
      invalidName: 'Gib einen Namen mit 1–200 Zeichen ein.',
      oneOffWithinValidity: 'Das einmalige Datum muss gültig sein und im Gültigkeitszeitraum liegen.',
      invalidAnnualDate: 'Wähle einen gültigen Monat und Tag.',
      invalidRuleType: 'Wähle einen verfügbaren Regeltyp.',
      writeError: 'Der Feiertag konnte nicht gespeichert werden. Bitte erneut versuchen.',
    },
    login: {
      title: 'Anmeldung',
      welcome: 'Willkommen zurück 👋',
      email: 'E-Mail',
      password: 'Passwort',
      rememberMe: 'Angemeldet bleiben',
      forgotPassword: 'Passwort vergessen?',
      submit: 'Anmelden',
      loading: 'Anmeldung...',
      or: 'oder',
      signInWithMicrosoft: 'Mit Microsoft anmelden',
      microsoftLoading: 'Microsoft wird geöffnet...',
      signInWithGoogle: 'Mit Google anmelden',
      googleLoading: 'Google wird geöffnet...',
      googleError: 'Google-Anmeldung fehlgeschlagen',
      oidcError: 'Anmeldung fehlgeschlagen',
      emailRequired: 'E-Mail ist erforderlich',
      passwordRequired: 'Passwort ist erforderlich',
      invalidCredentials: 'Falsche Anmeldedaten',
      connectionError: 'Verbindungsproblem',
    },
    logout: {
      imageAlt: 'Abmeldung',
      heroTitle: 'Bis zum nächsten Mal',
      heroText: 'Deine Sitzung ist beendet. Melde dich wieder an, wenn du weiterarbeiten möchtest.',
      status: 'Sitzung beendet',
      title: 'Abmeldung',
      goodbyeNamed: 'Auf Wiedersehen, {name}.',
      description: 'Du wurdest sicher abgemeldet. Für geschützte Bereiche ist eine neue Anmeldung erforderlich.',
      login: 'Anmelden',
      home: 'Startseite',
    },
    notFound: {
      eyebrow: 'Fehler 404',
      title: 'Diese Seite wurde nicht gefunden.',
      description: 'Die aufgerufene Adresse existiert nicht oder wurde verschoben. Kehre zur Startseite zurück oder öffne die vorherige Seite.',
      home: 'Zur Startseite',
      back: 'Zurück',
      imageAlt: 'Wüstenlandschaft',
      imageCaption: 'Manchmal führt ein Weg ins Leere.',
    },
    signup: {
      headline: 'Effizienz freischalten.',
      headlineAccent: 'Kostenloses Konto.',
      intro: 'Mehr Zeit für deine Mission, weniger für Verwaltung.',
      form: {
        title: 'Konto erstellen',
        subtitle: 'Starte mit deinem kostenlosen Workspace.',
        organizationSection: 'Deine Organisation',
        accountSection: 'Dein Administratorkonto',
        accountHint: 'Mit diesen Angaben meldest du dich an und verwaltest dein Team.',
        country: 'Land',
        currency: 'Basiswährung',
        city: 'Stadt',
        optional: '(optional)',
        cityPlaceholder: 'Stadt deiner Organisation',
        locationHint: 'Land und Stadt deiner Organisation werden für die Berechnung der Arbeitszeiten und Feiertage verwendet.',
        countryRequired: 'Bitte wähle ein Land',
        currencyRequired: 'Bitte wähle eine Währung',
        company: 'Name der Organisation',
        email: 'E-Mail',
        firstName: 'Vorname',
        lastName: 'Nachname',
        newPassword: 'Neues Passwort',
        confirmPassword: 'Passwort wiederholen',
        companyPlaceholder: 'Firma',
        emailPlaceholder: 'email@firma.com',
        emailPrivacyNotice: 'Wir verwenden deine E-Mail nur zur Einrichtung deines Kontos. Kein Spam.',
        firstNamePlaceholder: 'Vorname',
        lastNamePlaceholder: 'Nachname',
        submit: 'Kostenloses Konto erstellen',
        companyRequired: 'Firmenname ist erforderlich',
        emailRequired: 'E-Mail ist erforderlich',
        firstNameRequired: 'Vorname ist erforderlich',
        lastNameRequired: 'Nachname ist erforderlich',
        passwordRequired: 'Passwort ist erforderlich',
        confirmRequired: 'Bitte wiederholen',
        passwordMismatch: 'Die beiden Passwörter sind nicht identisch.',
        captchaRequired: 'Bitte löse die Sicherheitsprüfung.',
        captchaInvalid: 'Die Rechenaufgabe ist falsch. Bitte erneut versuchen.',
        captchaTitle: 'Sicherheitsprüfung',
        captchaRegenerate: 'Neue Zahlen',
        captchaHint: 'Beweise, dass du ein Mensch bist, indem du die Rechenaufgabe löst.',
        short: 'zu kurz',
        weak: 'schwach',
        okay: 'okay',
        good: 'gut',
        strong: 'stark',
      },
    },
  },
};
